import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Project } from '../../types';
import { dossierAPI, analysisAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { LockBar } from '../dossier/LockBar';
import { useSectionClear } from '../dossier/useSectionClear';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Stakeholder, MATRIX_CELLS, aanpakVoor, migrateStakeholders, stakeholdersToText, seedFromFase1, mergeProposals, saveStakeholdersInDossier } from './StakeholdersEditor';
import { RichText, htmlToPlain, htmlHasText } from '../common/RichText';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';

interface OmgevingsanalyseModuleProps {
  project: Project;
}

type ContentKey =
  | 'gebiedskenmerken'
  | 'stakeholders'
  | 'sentimenten'
  | 'belangenInformatiebehoeften'
  | 'kansen'
  | 'risicosGevoeligheden'
  | 'impact';

const FIELDS: { key: ContentKey; label: string; hint: string }[] = [
  { key: 'gebiedskenmerken', label: 'Gebiedskenmerken', hint: 'Type gebied, bebouwing, demografie en belangrijke voorzieningen.' },
  { key: 'sentimenten', label: 'Sentimenten', hint: 'Welke sentimenten leven er, en bij wie?' },
  { key: 'belangenInformatiebehoeften', label: 'Belangen & informatiebehoeften', hint: 'Wat willen en verwachten de betrokkenen; welke informatie hebben ze nodig?' },
  { key: 'kansen', label: 'Kansen', hint: 'Kansen voor het project en de communicatie.' },
  { key: 'risicosGevoeligheden', label: "Risico's & gevoeligheden", hint: 'Waar moeten we rekening mee houden?' },
  { key: 'impact', label: 'Impact', hint: 'Aard en omvang van de verwachte impact op de omgeving.' },
];

const emptyContent: Record<ContentKey, string> = {
  gebiedskenmerken: '', stakeholders: '', sentimenten: '', belangenInformatiebehoeften: '',
  kansen: '', risicosGevoeligheden: '', impact: '',
};

// Vastgelegde samenvatting van de gebiedskaart in het dossier (reproduceerbaar bij Lock).
interface GebiedSnapshot {
  overgenomenOp: string;
  buurtNaam?: string;
  areaSqmHa?: number | null;
  demografie?: { inwoners?: number | null; percentage65Plus?: number | null; gemiddeldInkomen?: number | null };
  bedrijvenTotaal?: number | null;
  organisatiesCount?: number | null;
  voorzieningenCount?: number | null;
  gebiedsprofielSamenvatting?: string;
  aandachtspunten?: string[];
}

const fmtNum = (n?: number | null): string => (typeof n === 'number' && !isNaN(n) ? n.toLocaleString('nl-NL') : '—');
const fmtEuro = (n?: number | null): string => (typeof n === 'number' && n > 0 ? `€ ${n.toLocaleString('nl-NL')}` : '—');
const SENT_COLORS: Record<string, string> = { positief: '#1f9d55', negatief: '#b91c1c', gemengd: '#d97706', neutraal: '#6b7280' };
const sentColor = (v?: string): string => SENT_COLORS[String(v || '').toLowerCase()] || '#6b7280';
const scoreColor = (n?: number): string => (typeof n === 'number' && n >= 20 ? '#1f9d55' : typeof n === 'number' && n <= -20 ? '#b91c1c' : '#6b7280');

const cardStyle: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, background: '#fff' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 2 };
const hintStyle: React.CSSProperties = { fontSize: 12.5, color: '#8a90a0', marginBottom: 10 };
function bronBadgeStyle(bron?: 'handmatig' | 'ai'): React.CSSProperties {
  const ai = bron === 'ai';
  return { fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: ai ? faseKleur(2).soft : '#f1f3f6', color: ai ? '#1e3a2f' : '#8a90a0' };
}
function textareaStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%', minHeight: 84, resize: 'vertical', padding: '10px 12px', borderRadius: 8,
    border: '1px solid #d8dce4', fontSize: 14, lineHeight: 1.55, fontFamily: 'inherit',
    color: '#20242e', background: disabled ? '#f6f7f9' : '#fff', boxSizing: 'border-box',
  };
}

export const OmgevingsanalyseModule: React.FC<OmgevingsanalyseModuleProps> = ({ project }) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [content, setContent] = useState<Record<ContentKey, string>>(emptyContent);
  const [status, setStatus] = useState<'open' | 'locked'>('open');
  const [lockedAt, setLockedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [area, setArea] = useState<any | null>(null);
  const [snapshot, setSnapshot] = useState<GebiedSnapshot | null>(null);
  const [overnemenBusy, setOvernemenBusy] = useState(false);
  const [bronMap, setBronMap] = useState<Partial<Record<ContentKey, 'handmatig' | 'ai'>>>({});
  const [useArea, setUseArea] = useState(true);
  const [lastAssist, setLastAssist] = useState<{ filled: number; areaUsed: string } | null>(null);
  const [nieuws, setNieuws] = useState<any | null>(null);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [searchParams] = useSearchParams();
  const legacyStakeholderParam = searchParams.get('stakeholder');

  const locked = status === 'locked';

  const loadDossier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      const fase1 = (res?.dossier?.sections?.projectverkenning?.content) || {};
      // Fase 1-veld kan rich-text (HTML) zijn → naar platte tekst en opsommingstekens strippen
      // zodat de deterministische stakeholder-parser (seedFromFase1) er goed mee overweg kan.
      const fase1St = htmlToPlain(fase1.belangrijksteStakeholders).replace(/^[-•*]\s+/gm, '').trim();
      const section = res?.dossier?.sections?.omgevingsanalyse;
      if (section) {
        const sc: any = section.content || {};
        const { gebiedskaartSnapshot, stakeholdersList, _bron, nieuwsSentiment, ...fields } = sc;
        setContent({ ...emptyContent, ...fields });
        setSnapshot(gebiedskaartSnapshot || null);
        setBronMap(_bron || {});
        setNieuws(nieuwsSentiment || null);
        let sh = migrateStakeholders(stakeholdersList, fields.stakeholders);
        // Nog nooit stakeholders vastgelegd? Neem de Belangrijkste stakeholders uit Fase 1 als startpunt.
        let autoSeeded = false;
        if (sh.length === 0 && stakeholdersList === undefined && fase1St.trim()) { sh = seedFromFase1(fase1St); autoSeeded = true; }
        setStakeholders(sh);
        setStatus(section.status === 'locked' ? 'locked' : 'open');
        setLockedAt(section.lockedAt || null);
        // Automatische overname meteen vastleggen zodat die bewaard blijft (gebeurt maar één keer).
        if (autoSeeded && section.status !== 'locked') {
          try { await dossierAPI.saveSection(project.id, 'omgevingsanalyse', { ...fields, stakeholders: stakeholdersToText(sh), stakeholdersList: sh }); } catch (e) { /* niet kritisch */ }
        }
      } else if (fase1St.trim()) {
        const sh = seedFromFase1(fase1St);
        setStakeholders(sh);
        try { await dossierAPI.saveSection(project.id, 'omgevingsanalyse', { stakeholders: stakeholdersToText(sh), stakeholdersList: sh }); } catch (e) { /* niet kritisch */ }
      }
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { loadDossier(); }, [loadDossier]);

  // Beheerders kunnen deze fase leegmaken om in een testsessie opnieuw te vullen.
  const { canClear, clearing, doClear } = useSectionClear(project.id, project.name, 'omgevingsanalyse', "Omgevingsanalyse", { stakeholdersList: [] }, loadDossier);

  // Gebiedskaart-data ophalen (indien een gebied is getekend) zodat die terugkomt in Fase 2.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await analysisAPI.getArea(project.id);
        if (!cancelled) setArea(res?.data || null);
      } catch {
        if (!cancelled) setArea(null); // 404 = nog geen gebiedskaart getekend
      }
    })();
    return () => { cancelled = true; };
  }, [project.id]);

  // Stakeholders hebben een eigen pagina; oude links (?stakeholder=…) daarheen doorsturen.
  useEffect(() => {
    if (legacyStakeholderParam) {
      navigate(`/project/${project.id}/stakeholders?stakeholder=${encodeURIComponent(legacyStakeholderParam)}`, { replace: true });
    }
  }, [legacyStakeholderParam, navigate, project.id]);

  const update = (key: ContentKey, value: string) => {
    setContent((c) => ({ ...c, [key]: value }));
    setBronMap((b) => ({ ...b, [key]: 'handmatig' })); // eigen invoer = handmatig
  };

  // Stakeholderlijst + gebiedskaart-snapshot meenemen bij elke save/lock.
  const buildPayload = (c: Record<ContentKey, string>, snap?: GebiedSnapshot, sh: Stakeholder[] = stakeholders) => {
    const s = snap || snapshot;
    return {
      ...c,
      stakeholders: stakeholdersToText(sh), // tekstsamenvatting voor downstream fasen
      stakeholdersList: sh,
      _bron: bronMap, // herkomst per veld (handmatig/ai)
      nieuwsSentiment: nieuws || undefined,
      ...(s ? { gebiedskaartSnapshot: s } : {}),
    };
  };

  const buildSnapshot = (): GebiedSnapshot => {
    const d = area?.demographics || {};
    return {
      overgenomenOp: new Date().toISOString(),
      buurtNaam: area?.buurtNaam || '',
      areaSqmHa: area?.areaSqm ? Math.round(area.areaSqm / 1000) / 10 : null,
      demografie: { inwoners: d.inwoners ?? null, percentage65Plus: d.percentage65Plus ?? null, gemiddeldInkomen: d.gemiddeldInkomen ?? null },
      bedrijvenTotaal: area?.bedrijven?.totaal ?? null,
      organisatiesCount: area?.organisaties?.totaal ?? null,
      voorzieningenCount: area?.voorzieningen?.items?.length ?? null,
      gebiedsprofielSamenvatting: area?.gebiedsprofiel?.narrative || '',
      aandachtspunten: area?.gebiedsprofiel?.aandachtspunten || [],
    };
  };

  const overnemen = async () => {
    if (!area) return;
    setOvernemenBusy(true);
    const snap = buildSnapshot();
    try {
      await dossierAPI.saveSection(project.id, 'omgevingsanalyse', buildPayload(content, snap));
      setSnapshot(snap);
      toast.success('Overgenomen', 'De gebiedsanalyse is vastgelegd in het projectdossier.');
    } catch (e: any) {
      toast.error('Overnemen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setOvernemenBusy(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await dossierAPI.saveSection(project.id, 'omgevingsanalyse', buildPayload(content));
      toast.success('Concept opgeslagen', 'De omgevingsanalyse is als concept vastgelegd.');
    } catch (e: any) {
      toast.error('Opslaan mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setSaving(false); }
  };

  const runAssist = async () => {
    setAiLoading(true);
    try {
      const res = await dossierAPI.assistOmgevingsanalyse(project.id, useArea);
      const analyse = res?.analyse || {};
      // Vul alleen nog lege velden met het AI-concept; bestaande input blijft behouden.
      const filledKeys: ContentKey[] = [];
      (Object.keys(emptyContent) as ContentKey[]).forEach((k) => {
        if (k === 'stakeholders') return; // stakeholders zijn nu een gestructureerde lijst
        if (!content[k]?.trim() && typeof analyse[k] === 'string' && analyse[k].trim()) filledKeys.push(k);
      });
      if (filledKeys.length) {
        setContent((c) => { const next = { ...c }; filledKeys.forEach((k) => { next[k] = analyse[k]; }); return next; });
        setBronMap((b) => { const nb = { ...b }; filledKeys.forEach((k) => { nb[k] = 'ai'; }); return nb; }); // AI-herkomst
      }
      setLastAssist({ filled: filledKeys.length, areaUsed: res?.areaUsed || '' });
      const filled = filledKeys.length;
      // Gestructureerde stakeholder-voorstellen van de AI toevoegen (bestaande niet overschrijven).
      const voorstel = Array.isArray(res?.stakeholders) ? res.stakeholders : [];
      let nieuweLijst: Stakeholder[] | null = null;
      if (voorstel.length) {
        nieuweLijst = mergeProposals(stakeholders, voorstel);
      } else if (typeof analyse.stakeholders === 'string' && analyse.stakeholders.trim() && stakeholders.length === 0) {
        nieuweLijst = migrateStakeholders(null, analyse.stakeholders);
      }
      const stakeholdersAdded = nieuweLijst ? nieuweLijst.length - stakeholders.length : 0;
      if (nieuweLijst && stakeholdersAdded > 0) {
        setStakeholders(nieuweLijst);
        // Direct vastleggen: stakeholders staan op een eigen pagina en moeten daar meteen zichtbaar zijn.
        // De ingevulde analysevelden blijven concept tot 'Concept opslaan'.
        if (!locked) { try { await saveStakeholdersInDossier(project.id, nieuweLijst); } catch (e) { /* 'Concept opslaan' neemt de lijst alsnog mee */ } }
      }
      const extra = stakeholdersAdded > 0 ? ` ${stakeholdersAdded} stakeholder(s) voorgesteld — bekijk ze bij Stakeholders.` : '';
      toast.success('AI-concept ingevuld', (filled > 0 ? `${filled} veld(en) ingevuld.` : 'Velden waren al ingevuld.') + extra + ' Verrijk gerust met lokale kennis.');
    } catch (e: any) {
      toast.error('AI-ondersteuning mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally { setAiLoading(false); }
  };

  const canLock = Boolean(stakeholders.some((s) => s.naam.trim()) && htmlHasText(content.risicosGevoeligheden));

  const doLock = async () => {
    setLocking(true);
    try {
      const res = await dossierAPI.lockSection(project.id, 'omgevingsanalyse', buildPayload(content));
      setStatus('locked');
      setLockedAt(res?.section?.lockedAt || new Date().toISOString());
      toast.success('Fase gevalideerd', 'De omgevingsanalyse is vastgelegd in het projectdossier.');
    } catch (e: any) {
      toast.error('Valideren mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  const doUnlock = async () => {
    setLocking(true);
    try {
      await dossierAPI.unlockSection(project.id, 'omgevingsanalyse');
      setStatus('open');
      toast.success('Fase heropend', 'Je kunt de omgevingsanalyse nu aanpassen en opnieuw valideren.');
    } catch (e: any) {
      toast.error('Heropenen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Projectdossier laden…</div>;

  const stakeholderVerdeling = ['nauw', 'tevreden', 'informeren', 'monitoren'].map((key) => {
    const cell = MATRIX_CELLS.find((c) => c.key === key)!;
    return { ...cell, aantal: stakeholders.filter((st) => aanpakVoor(st.invloed, st.belang).key === key).length };
  });

  const view = area ? {
    buurtNaam: area.buurtNaam as string | null,
    inwoners: area.demographics?.inwoners as number | undefined,
    p65: area.demographics?.percentage65Plus as number | undefined,
    inkomen: area.demographics?.gemiddeldInkomen as number | undefined,
    ha: area.areaSqm ? Math.round(area.areaSqm / 1000) / 10 : null,
    narrative: (area.gebiedsprofiel?.narrative as string) || '',
    aandachtspunten: (area.gebiedsprofiel?.aandachtspunten as string[]) || [],
    organisaties: area.organisaties?.totaal as number | undefined,
    bedrijven: area.bedrijven?.totaal as number | undefined,
    voorzieningen: (area.voorzieningen?.items?.length as number | undefined),
  } : (snapshot ? {
    buurtNaam: snapshot.buurtNaam || null,
    inwoners: snapshot.demografie?.inwoners ?? undefined,
    p65: snapshot.demografie?.percentage65Plus ?? undefined,
    inkomen: snapshot.demografie?.gemiddeldInkomen ?? undefined,
    ha: snapshot.areaSqmHa ?? null,
    narrative: snapshot.gebiedsprofielSamenvatting || '',
    aandachtspunten: snapshot.aandachtspunten || [],
    organisaties: snapshot.organisatiesCount ?? undefined,
    bedrijven: snapshot.bedrijvenTotaal ?? undefined,
    voorzieningen: snapshot.voorzieningenCount ?? undefined,
  } : null);

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1120, margin: '0 auto' }}>
      <FaseTag nr={2} />
      <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: BRAND.groen, letterSpacing: '-0.3px' }}>De omgevingsanalyse</h1>
      <p style={{ margin: '0 0 20px', color: '#555', fontSize: 15, lineHeight: 1.6 }}>
        Breng de sociale én fysieke omgeving in beeld: stakeholders, sentimenten, belangen, kansen,
        risico's en impact. AI doet een eerste voorstel; verrijk dit met lokale kennis en valideer.
      </p>

      {/* Gebiedskaart — compacte samenvatting (zelfde vorm als Nieuws & sentiment en Stakeholders); volledige kaart op eigen pagina */}
      <div style={{ ...cardStyle, marginBottom: 20, padding: 0, border: '1px solid #ececf1', overflow: 'hidden' }}>
        <button onClick={() => navigate(`/project/${project.id}/gebiedsanalyse`)}
          style={{ width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, padding: 20, border: 'none', background: 'transparent', fontFamily: 'inherit' }}>
          <span style={{ width: 42, height: 42, borderRadius: 11, background: faseKleur(2).soft, color: BRAND.groen, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
              Gebiedskaart{view?.buurtNaam ? <span style={{ fontWeight: 500, color: '#8a90a0' }}> · {view.buurtNaam}</span> : null}
            </span>
            {view ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                {[
                  typeof view.inwoners === 'number' ? `${fmtNum(view.inwoners)} inwoners` : '',
                  typeof view.p65 === 'number' ? `${view.p65}% 65-plus` : '',
                  typeof view.inkomen === 'number' && view.inkomen > 0 ? `${fmtEuro(view.inkomen)} gem. inkomen` : '',
                  view.ha != null ? `${view.ha} ha` : '',
                ].filter(Boolean).map((t) => (
                  <span key={t} style={{ fontSize: 12, fontWeight: 600, color: BRAND.groen, background: BRAND.papier, border: `1px solid ${BRAND.rand}`, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>{t}</span>
                ))}
              </span>
            ) : (
              <span style={{ display: 'block', fontSize: 12.5, color: '#8a90a0', marginTop: 4 }}>Open data over het gebied: demografie, voorzieningen, bedrijven en werkzaamheden. Teken het projectgebied om te beginnen.</span>
            )}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: BRAND.groen, flexShrink: 0 }}>
            Openen
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </span>
        </button>
        {/* Gebiedsgegevens vastleggen in het dossier: de AI gebruikt ze als context */}
        {!locked && (area || snapshot) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '10px 20px', borderTop: '1px solid #f0f1f4', background: '#fafbfc', fontSize: 12.5, color: '#6b7280' }}>
            <span>
              {snapshot
                ? <><span style={{ color: '#1f7a44', fontWeight: 600 }}>In het dossier</span>{snapshot.overgenomenOp ? ` sinds ${new Date(snapshot.overgenomenOp).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}</>
                : 'Nog niet overgenomen in het dossier'}
              {' '}— de AI gebruikt deze gegevens als context.
            </span>
            {area && (
              <button onClick={overnemen} disabled={overnemenBusy}
                style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, border: snapshot ? '1px solid #cbd5e1' : 'none', background: snapshot ? '#fff' : BRAND.groen, color: snapshot ? '#334155' : '#fff', cursor: overnemenBusy ? 'default' : 'pointer', opacity: overnemenBusy ? 0.6 : 1 }}>
                {snapshot ? (overnemenBusy ? 'Verversen…' : 'Ververs') : (overnemenBusy ? 'Overnemen…' : 'Overnemen in dossier')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Sentiment- & mediadashboard (offerte Stap 2) — compacte samenvatting, volledige weergave op eigen pagina */}
      <button onClick={() => navigate(`/project/${project.id}/mediadashboard`)}
        style={{ ...cardStyle, marginBottom: 20, width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, border: '1px solid #ececf1' }}>
        <span style={{ width: 42, height: 42, borderRadius: 11, background: faseKleur(2).soft, color: '#1e3a2f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v10a2 2 0 0 0 2 2z" /><line x1="6" y1="9" x2="13" y2="9" /><line x1="6" y1="13" x2="13" y2="13" /></svg>
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Nieuws &amp; sentiment</span>
          {nieuws ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: sentColor(nieuws.overall), borderRadius: 999, padding: '3px 11px', textTransform: 'capitalize' }}>{nieuws.overall}</span>
              <span style={{ fontSize: 12.5, color: '#6b7280' }}>{Array.isArray(nieuws.items) ? nieuws.items.length : 0} bericht(en) · score <strong style={{ color: scoreColor(nieuws.score) }}>{nieuws.score > 0 ? `+${nieuws.score}` : nieuws.score}</strong></span>
            </span>
          ) : (
            <span style={{ display: 'block', fontSize: 12.5, color: '#8a90a0', marginTop: 4 }}>Recente nieuwsberichten + AI-sentiment. Stel zelf zoektermen in.</span>
          )}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#1e3a2f', flexShrink: 0 }}>
          Openen
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </span>
      </button>

      {/* Stakeholders & stakeholderanalyse — compacte samenvatting, volledige analyse op eigen pagina */}
      <button onClick={() => navigate(`/project/${project.id}/stakeholders`)}
        style={{ ...cardStyle, marginBottom: 20, width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, border: '1px solid #ececf1' }}>
        <span style={{ width: 42, height: 42, borderRadius: 11, background: faseKleur(2).soft, color: BRAND.groen, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Stakeholders &amp; stakeholderanalyse</span>
          {stakeholders.length > 0 ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <span style={{ fontSize: 12.5, color: '#6b7280', marginRight: 2 }}><strong style={{ color: '#1a1a2e' }}>{stakeholders.length}</strong> stakeholder{stakeholders.length === 1 ? '' : 's'}</span>
              {stakeholderVerdeling.filter((c) => c.aantal > 0).map((c) => (
                <span key={c.key} style={{ fontSize: 12, fontWeight: 600, color: c.color, background: c.bg, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>{c.label} · {c.aantal}</span>
              ))}
            </span>
          ) : (
            <span style={{ display: 'block', fontSize: 12.5, color: '#8a90a0', marginTop: 4 }}>Breng in kaart wie geraakt wordt of invloed heeft, en welke aanpak daarbij past.</span>
          )}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: BRAND.groen, flexShrink: 0 }}>
          Openen
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </span>
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 340px', gap: 20, alignItems: 'start' }}>
        {/* LINKERKOLOM */}
        <div style={{ minWidth: 0 }}>
      {/* Analyse-velden */}
      <div style={{ display: 'grid', gap: 16 }}>
        {FIELDS.map((f) => (
          <div key={f.key} style={cardStyle}>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              {f.label}
              {htmlHasText(content[f.key]) && <span style={bronBadgeStyle(bronMap[f.key])}>{bronMap[f.key] === 'ai' ? 'AI-voorstel' : 'Handmatig'}</span>}
            </label>
            <div style={hintStyle}>{f.hint}</div>
            <RichText value={content[f.key]} onChange={(html) => update(f.key, html)} disabled={locked} placeholder="Vul in…" />
          </div>
        ))}
      </div>

      {!locked && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={saveDraft}
            disabled={saving}
            style={{ padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Opslaan…' : 'Concept opslaan'}
          </button>
        </div>
      )}
        </div>{/* /LINKERKOLOM */}

        {/* RECHTERKOLOM — AI-ondersteuning */}
        <div>
          <div style={{ background: faseKleur(2).soft, border: `1px solid ${faseKleur(2).border}`, borderRadius: 12, padding: 18, position: isMobile ? 'static' : 'sticky', top: 16 }}>
            <div style={{ ...labelStyle, marginBottom: 2 }}>AI-ondersteuning</div>
            <div style={hintStyle}>AI stelt een concept-analyse voor op basis van de projectverkenning{area ? ' en de gebiedskaart' : ''}. Jij verrijkt en beslist; alleen lege velden worden ingevuld.</div>
            {area && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#4b5563', margin: '0 0 12px', cursor: locked ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={useArea} disabled={locked} onChange={(e) => setUseArea(e.target.checked)} />
                Gebiedskaart-data meenemen
              </label>
            )}
            <button
              onClick={runAssist}
              disabled={aiLoading || locked}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '10px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: '#1e3a2f', color: '#fff', cursor: aiLoading || locked ? 'default' : 'pointer', opacity: aiLoading || locked ? 0.6 : 1 }}
            >
              {aiLoading ? 'AI stelt op…' : 'Concept-analyse voorstellen'}
            </button>
            {lastAssist && (
              <div style={{ marginTop: 12, fontSize: 12.5, color: '#4b5563' }}>
                <div>{lastAssist.filled > 0 ? `${lastAssist.filled} veld(en) ingevuld` : 'Geen lege velden ingevuld'}{lastAssist.areaUsed ? ' — mede op basis van de gebiedskaart.' : '.'}</div>
                {lastAssist.areaUsed && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: 'pointer', color: '#1e3a2f', fontWeight: 600 }}>Gebruikte gebiedsdata</summary>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 12, color: '#374151', background: '#fff', border: `1px solid ${faseKleur(2).border}`, borderRadius: 8, padding: 10, marginTop: 6 }}>{lastAssist.areaUsed}</pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>{/* /grid */}

      <LockBar
        status={status}
        onLock={doLock}
        onUnlock={doUnlock}
        lockedAt={lockedAt}
        canLock={canLock}
        busy={locking}
        faseLabel="Omgevingsanalyse"
        canClear={canClear}
        onClear={doClear}
        clearing={clearing}
      />
      {!canLock && !locked && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#8a90a0', textAlign: 'right' }}>
          Vul minimaal de stakeholders en de risico's/gevoeligheden in om te kunnen valideren.
        </div>
      )}
    </div>
  );
};
